import type { IntlShape } from 'react-intl';

import { aiSuggestionsMessages } from '@atlaskit/editor-common/ai-messages/ai-suggestions';

import type { ReviewResults } from '../../../pm-plugins/thinking-plugin';

export function getReviewCompleteMessage(
	formatMessage: IntlShape['formatMessage'],
	reviewResults: ReviewResults,
	reviewer?: string,
): string {
	const { count, hadExistingSuggestions } = reviewResults;
	if (count === 0) {
		return formatMessage(aiSuggestionsMessages.thinkingCompleteNoSuggestionsUpdated);
	}
	const message = reviewer
		? hadExistingSuggestions
			? aiSuggestionsMessages.thinkingCompleteNewSuggestionCount
			: aiSuggestionsMessages.thinkingCompleteSuggestionCount
		: hadExistingSuggestions
			? aiSuggestionsMessages.thinkingCompleteNewSuggestionCountWithoutReviewer
			: aiSuggestionsMessages.thinkingCompleteSuggestionCountWithoutReviewer;
	return formatMessage(message, { count, reviewer });
}

/**
 * Returns the domain-specific rotating messages shown while the AI
 * suggestions thinking bar is active.
 *
 * Kept in step with the product's own suggestions loading states (Confluence's suggestions
 * panel renders the same sequence), so the copy doesn't change when the user moves between
 * the editor bar and the panel.
 *
 * The first status ("Rovo is reviewing your page") is overridden to name the reviewing
 * agent — see `getReviewingContentWithAgentMessage` — once it resolves.
 */
export function getThinkingRotatingMessages(formatMessage: IntlShape['formatMessage']): string[] {
	return [
		formatMessage(aiSuggestionsMessages.thinkingReviewingContent),
		formatMessage(aiSuggestionsMessages.thinkingMayTakeAFewMinutes),
		formatMessage(aiSuggestionsMessages.thinkingGatheringContext),
		formatMessage(aiSuggestionsMessages.thinkingReviewingConsistency),
		formatMessage(aiSuggestionsMessages.thinkingCheckingWritingClarity),
		formatMessage(aiSuggestionsMessages.thinkingIdentifyingImprovements),
		formatMessage(aiSuggestionsMessages.thinkingGeneratingAiSuggestions),
		formatMessage(aiSuggestionsMessages.thinkingRefiningAiSuggestions),
		formatMessage(aiSuggestionsMessages.thinkingPrioritizingAiSuggestions),
	];
}

/**
 * The first-status text naming the reviewing agent, e.g. "{agentName} is analyzing".
 * Shown
 * (with the agent's avatar) in place of the agentless first status once the agent resolves.
 */
export function getReviewingContentWithAgentMessage(
	formatMessage: IntlShape['formatMessage'],
	agentName: string,
): string {
	return formatMessage(aiSuggestionsMessages.thinkingReviewingContentWithAgent, { agentName });
}
